import React from 'react';
import { IconChevronRight, IconChevronUp, IconChevronDown } from '@tabler/icons-react';
import type { Catalog } from '../../../entities/catalog';

interface TopicTreeProps {
  topics: Catalog[];
  selectedId: string;
  expanded: Set<string>;
  isAdmin?: boolean;
  onSelect: (id: string) => void;
  onToggle: (id: string) => void;
  onMove?: (topic: Catalog, dir: 'up' | 'down') => void;
}

const TopicTree: React.FC<TopicTreeProps> = ({ topics, selectedId, expanded, isAdmin, onSelect, onToggle, onMove }) => {
  const roots = topics.filter(t => !t.texts.parentId).sort((a, b) => a.order - b.order);
  const childrenOf = (id: string) =>
    topics.filter(t => t.texts.parentId === id).sort((a, b) => a.subcategory - b.subcategory);

  // siblings — отсортированная группа, в которой находится t; idx — позиция в ней.
  const row = (t: Catalog, level: number, siblings: Catalog[], idx: number) => {
    const kids = childrenOf(t.id);
    const hasKids = kids.length > 0;
    const isOpen = expanded.has(t.id);
    return (
      <React.Fragment key={t.id}>
        <div
          className={`kb-tree-item${t.id === selectedId ? ' active' : ''}`}
          style={{ paddingLeft: 12 + level * 16 }}
          onClick={() => onSelect(t.id)}
        >
          {hasKids ? (
            <button
              type="button"
              className={`kb-tree-caret${isOpen ? ' open' : ''}`}
              onClick={(e) => { e.stopPropagation(); onToggle(t.id); }}
              aria-label={isOpen ? 'Свернуть' : 'Развернуть'}
            >
              <IconChevronRight size={16} stroke={2.2} />
            </button>
          ) : (
            <span className="kb-tree-caret kb-tree-caret-empty" />
          )}
          <span className="kb-tree-label">{t.texts.title}</span>
          {isAdmin && onMove && (
            <span className="kb-tree-move">
              <button
                type="button" className="kb-tree-move-btn" title="Выше" disabled={idx === 0}
                onClick={(e) => { e.stopPropagation(); onMove(t, 'up'); }}
              >
                <IconChevronUp size={15} stroke={2.2} />
              </button>
              <button
                type="button" className="kb-tree-move-btn" title="Ниже" disabled={idx === siblings.length - 1}
                onClick={(e) => { e.stopPropagation(); onMove(t, 'down'); }}
              >
                <IconChevronDown size={15} stroke={2.2} />
              </button>
            </span>
          )}
        </div>
        {hasKids && isOpen && kids.map((k, i) => row(k, level + 1, kids, i))}
      </React.Fragment>
    );
  };

  return <nav className="kb-tree">{roots.map((r, i) => row(r, 0, roots, i))}</nav>;
};

export { TopicTree };
